import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useForm, usePage } from "@inertiajs/inertia-react";
import {
  CameraIcon,
  CheckCircleIcon,
  MapPinIcon,
  ShieldCheckIcon,
  WrenchScrewdriverIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import CustomerLayout from "../../../Layouts/CustomerLayout";
import Logo from "../../../Components/Logo";

const selectStyles = {
  control: (base, state) => ({
    ...base,
    borderRadius: 6,
    borderColor: state.isFocused ? "#EA580C" : "#D1D5DB",
    boxShadow: state.isFocused ? "0 0 0 1px #EA580C" : "none",
    minHeight: 42,
    "&:hover": { borderColor: "#EA580C" },
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: "#FFF7ED",
    borderRadius: 4,
  }),
  placeholder: (base) => ({ ...base, color: "#9CA3AF" }),
};

const inputClass =
  "block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm";

const trustPoints = [
  "Free to submit a request",
  "Vetted local tradespeople",
  "No obligation to hire",
];

const Field = ({ label, hint, children }) => (
  <div className="mb-6">
    <label className="block text-sm font-medium text-gray-900">{label}</label>
    {hint && <p className="mt-0.5 text-sm text-gray-500">{hint}</p>}
    <div className="mt-2">{children}</div>
  </div>
);

// Public inquiry form for Tradesman Domestic. Reachable without an account:
// anonymous submissions (including any attached photos) are stashed
// server-side and created after signup.
const RequestForm = ({ trades }) => {
  const { customer } = usePage().props;
  const [previews, setPreviews] = useState([]);

  const { data, setData, post, processing } = useForm({
    description: "",
    trade_ids: [],
    ideal_start_date: "",
    postcode: "",
    images: [],
  });

  const tradeOptions = trades.map((t) => ({ value: t.id, label: t.name }));

  // useForm().setData(key, value) rebuilds the whole data object from
  // whatever `data` was closed over when ITS OWN render happened — since
  // useForm returns a fresh, non-memoized setData every render, a callback
  // memoized with a narrow dependency array (like onDrop below, which only
  // needs to change when the file list changes) can end up holding a
  // setData closure from a stale, earlier render and silently wipe out
  // every other field. The functional form — setData(prev => ({...})) —
  // always resolves against the current state instead, exactly like
  // React's setState(prev => ...), so every update here goes through it.
  const onDrop = useCallback((acceptedFiles) => {
    const withPreviews = acceptedFiles.map((file) =>
      Object.assign(file, { preview: URL.createObjectURL(file) })
    );
    setPreviews((prev) => [...prev, ...withPreviews]);
    setData((prev) => ({ ...prev, images: [...prev.images, ...acceptedFiles] }));
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [] },
  });

  function removeImage(index) {
    setPreviews((prev) => prev.filter((_, i) => i !== index));
    setData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  }

  function submit(e) {
    e.preventDefault();
    post("/domestic/requests");
  }

  const form = (
    <form onSubmit={submit} className="max-w-2xl">
      <Field label="What needs doing?">
        <textarea
          rows={5}
          required
          value={data.description}
          onChange={(e) => setData("description", e.target.value)}
          placeholder="e.g. Leaking radiator in the upstairs bathroom, needs a plumber to take a look and replace the valve…"
          className={inputClass}
        />
      </Field>

      <Field
        label="Trades required"
        hint="Pick everything relevant — you'll get responses from tradespeople covering any of these."
      >
        <Select
          isMulti
          options={tradeOptions}
          styles={selectStyles}
          placeholder="Search trades…"
          value={tradeOptions.filter((o) => data.trade_ids.includes(o.value))}
          onChange={(selected) =>
            setData(
              "trade_ids",
              (selected || []).map((o) => o.value)
            )
          }
        />
      </Field>

      <Field label="Ideal start date" hint="Optional — a rough date is fine.">
        <DatePicker
          selected={data.ideal_start_date ? new Date(data.ideal_start_date) : null}
          onChange={(date) =>
            setData("ideal_start_date", date ? date.toISOString().slice(0, 10) : "")
          }
          minDate={new Date()}
          dateFormat="dd/MM/yyyy"
          placeholderText="Pick a date"
          className={`${inputClass} max-w-xs`}
        />
      </Field>

      <Field
        label="Property postcode"
        hint="Only your postcode district (e.g. KT22) is ever shown to tradespeople."
      >
        <div className="relative max-w-xs">
          <MapPinIcon className="pointer-events-none absolute top-1/2 left-3 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            required
            value={data.postcode}
            onChange={(e) => setData("postcode", e.target.value)}
            placeholder="e.g. KT22 7EX"
            className={`${inputClass} pl-10`}
          />
        </div>
      </Field>

      <Field
        label="Photos"
        hint="A picture is worth a thousand words — helps tradespeople quote accurately. Optional."
      >
        <div
          {...getRootProps()}
          className={`flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed px-6 py-8 text-center transition-colors ${
            isDragActive
              ? "border-orange-500 bg-orange-50"
              : "border-gray-300 bg-gray-50 hover:border-orange-400 hover:bg-orange-50/50"
          }`}
        >
          <input {...getInputProps()} />
          <CameraIcon className="h-8 w-8 text-gray-400" />
          <p className="mt-2 text-sm text-gray-600">
            {isDragActive
              ? "Drop your photos here…"
              : "Drag photos here, or click to browse"}
          </p>
          <p className="mt-0.5 text-xs text-gray-400">JPG or PNG</p>
        </div>

        {previews.length > 0 && (
          <ul className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-4">
            {previews.map((file, index) => (
              <li
                key={index}
                className="group relative aspect-square overflow-hidden rounded-md border border-gray-200 bg-white shadow-sm"
              >
                <img
                  src={file.preview}
                  alt=""
                  className="h-full w-full object-cover"
                />
                <button
                  type="button"
                  onClick={() => removeImage(index)}
                  className="absolute top-1 right-1 flex h-6 w-6 items-center justify-center rounded-full bg-gray-900/70 text-white opacity-0 transition-opacity group-hover:opacity-100"
                  aria-label="Remove photo"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </Field>

      <div className="flex flex-col gap-3 border-t border-gray-200 pt-6 sm:flex-row sm:items-center sm:gap-4">
        <button
          type="submit"
          disabled={processing || data.trade_ids.length === 0}
          className="inline-flex w-full items-center justify-center rounded-md bg-orange-600 px-6 py-3 text-base font-semibold text-white shadow-sm transition hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-40 sm:w-auto"
        >
          {customer ? "Submit request" : "Continue — create account & send"}
        </button>
        {!customer && (
          <p className="text-xs text-gray-500">
            Takes 30 seconds — we&apos;ll bring you right back here.
          </p>
        )}
      </div>
    </form>
  );

  if (customer) {
    return <CustomerLayout title="Submit a Request">{form}</CustomerLayout>;
  }

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-gray-200">
        <div className="mx-auto flex max-w-6xl items-center px-4 py-4 sm:px-6 lg:px-8">
          <Logo className="h-7 w-auto" />
        </div>
      </header>

      <div className="bg-gray-50">
        <div className="mx-auto max-w-3xl px-4 py-14 sm:px-6 lg:px-8">
          <div className="fade-rise flex items-center gap-2 text-sm font-medium text-brandblue">
            <ShieldCheckIcon className="h-5 w-5" />
            Tradesman Domestic
          </div>
          <h1 className="fade-rise fade-rise-1 mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            Get quotes from trusted local tradespeople
          </h1>
          <p className="fade-rise fade-rise-2 mt-3 max-w-xl text-base text-gray-600">
            Tell us what needs doing and we&apos;ll put your request straight
            in front of vetted tradespeople in your area — no call centre, no
            waiting around.
          </p>

          <ul className="fade-rise fade-rise-3 mt-6 flex flex-col gap-3 sm:flex-row sm:gap-8">
            {trustPoints.map((point) => (
              <li key={point} className="flex items-center gap-2 text-sm text-gray-700">
                <CheckCircleIcon className="h-5 w-5 flex-shrink-0 text-green-600" />
                {point}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="fade-rise fade-rise-4 rounded-xl border border-gray-200 bg-white p-6 shadow-sm sm:p-8">
          {form}
        </div>
      </div>
    </div>
  );
};

export default RequestForm;
